import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
  } from '../ui/dialog';
  import { Button } from '../ui/button';
  import { Badge } from '../ui/badge';
  import { Separator } from '../ui/separator';
  import { AdminLog } from '../../lib/adminApi';
  import {
    User,
    Store,
    Calendar,
    Clock,
    Shield,
    Globe,
    Monitor,
    FileText,
    AlertCircle,
    CheckCircle,
    XCircle,
    Info,
  } from 'lucide-react';
  import { format } from 'date-fns';
  import { es } from 'date-fns/locale';
  
  interface LogDetailsModalProps {
    log: AdminLog | null;
    open: boolean;
    onClose: () => void;
  }
  
  export function LogDetailsModal({
    log,
    open,
    onClose,
  }: LogDetailsModalProps) {
    if (!log) return null;
  
    const formatDate = (dateString: string) => {
      try {
        return format(new Date(dateString), "dd 'de' MMMM 'de' yyyy, HH:mm:ss", { locale: es });
      } catch {
        return 'Fecha inválida';
      }
    };
  
    const getActionIcon = (actionType: string) => {
      switch (actionType) {
        case 'activate_account':
        case 'register_payment':
          return <CheckCircle className="h-5 w-5 text-green-600" />;
        case 'suspend_account':
          return <XCircle className="h-5 w-5 text-red-600" />;
        case 'extend_trial':
          return <Clock className="h-5 w-5 text-yellow-600" />;
        case 'payment_receipt_uploaded':
          return <AlertCircle className="h-5 w-5 text-blue-600" />;
        default:
          return <Info className="h-5 w-5 text-gray-600" />;
      }
    };
  
    const getActionText = (actionType: string) => {
      const actions: Record<string, string> = {
        'activate_account': 'Cuenta activada',
        'suspend_account': 'Cuenta suspendida',
        'extend_trial': 'Trial extendido',
        'register_payment': 'Pago registrado',
        'payment_receipt_uploaded': 'Comprobante de pago subido',
        'trial_extension_request': 'Solicitud de extensión de trial',
        'shop_contact_request': 'Contacto de negocio',
        'login': 'Inicio de sesión',
        'logout': 'Cierre de sesión',
        'create_shop': 'Negocio creado',
        'update_shop': 'Negocio actualizado',
      };
      
      return actions[actionType] || actionType.replace(/_/g, ' ');
    };
  
    const getSeverityColor = (actionType: string) => {
      switch (actionType) {
        case 'activate_account':
        case 'register_payment':
          return 'bg-green-50 border-green-200';
        case 'suspend_account':
          return 'bg-red-50 border-red-200';
        case 'extend_trial':
          return 'bg-yellow-50 border-yellow-200';
        default:
          return 'bg-gray-50 border-gray-200';
      }
    };
  
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Detalles del Log de Auditoría
            </DialogTitle>
            <DialogDescription>
              Información completa de la acción registrada
            </DialogDescription>
          </DialogHeader>
  
          <div className="space-y-6">
            {/* Encabezado con acción */}
            <div className={`p-4 rounded-lg border ${getSeverityColor(log.actionType)}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {getActionIcon(log.actionType)}
                  <div>
                    <h3 className="font-semibold text-lg">{getActionText(log.actionType)}</h3>
                    <p className="text-sm text-gray-600">ID: {log.id}</p>
                  </div>
                </div>
                <Badge variant="outline">
                  {formatDate(log.createdAt)}
                </Badge>
              </div>
            </div>
  
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Información del Administrador */}
              <div className="space-y-4">
                <h4 className="font-semibold text-lg flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Información del Administrador
                </h4>
  
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <User className="h-4 w-4 text-gray-400" />
                    <div>
                      <div className="text-sm text-gray-600">Nombre</div>
                      <div className="font-medium">{log.admin?.fullName || 'Sistema Automático'}</div>
                    </div>
                  </div>
  
                  <div className="flex items-center gap-3">
                    <Shield className="h-4 w-4 text-gray-400" />
                    <div>
                      <div className="text-sm text-gray-600">Usuario</div>
                      <div className="font-medium">@{log.admin?.username || 'system'}</div>
                    </div>
                  </div>
  
                  {log.adminUserId && (
                    <div className="flex items-center gap-3">
                      <Info className="h-4 w-4 text-gray-400" />
                      <div>
                        <div className="text-sm text-gray-600">ID de Admin</div>
                        <div className="font-mono text-sm">{log.adminUserId}</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
  
              {/* Información Técnica */}
              <div className="space-y-4">
                <h4 className="font-semibold text-lg flex items-center gap-2">
                  <Monitor className="h-4 w-4" />
                  Información Técnica
                </h4>
  
                <div className="space-y-3">
                  {log.ipAddress && (
                    <div className="flex items-center gap-3">
                      <Globe className="h-4 w-4 text-gray-400" />
                      <div>
                        <div className="text-sm text-gray-600">Dirección IP</div>
                        <div className="font-medium">{log.ipAddress}</div>
                      </div>
                    </div>
                  )}
  
                  {log.userAgent && (
                    <div className="flex items-center gap-3">
                      <Monitor className="h-4 w-4 text-gray-400" />
                      <div>
                        <div className="text-sm text-gray-600">Navegador/Dispositivo</div>
                        <div className="font-medium text-sm truncate">{log.userAgent}</div>
                      </div>
                    </div>
                  )}
  
                  <div className="flex items-center gap-3">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    <div>
                      <div className="text-sm text-gray-600">Fecha y hora exacta</div>
                      <div className="font-medium">{formatDate(log.createdAt)}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
  
            {/* Información del Negocio (si aplica) */}
            {log.shopId && (
              <>
                <Separator />
                <div className="space-y-4">
                  <h4 className="font-semibold text-lg flex items-center gap-2">
                    <Store className="h-4 w-4" />
                    Información del Negocio
                  </h4>
  
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <Store className="h-4 w-4 text-gray-400" />
                      <div>
                        <div className="text-sm text-gray-600">ID del Negocio</div>
                        <div className="font-mono text-sm">{log.shopId}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
  
            {/* Detalles de la Acción */}
            {log.details && (
              <>
                <Separator />
                <div className="space-y-4">
                  <h4 className="font-semibold text-lg flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Detalles de la Acción
                  </h4>
  
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <pre className="text-sm whitespace-pre-wrap font-mono">
                      {JSON.stringify(log.details, null, 2)}
                    </pre>
                  </div>
                </div>
              </>
            )}
  
            {/* Resumen */}
            <Separator />
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h4 className="font-semibold text-blue-900 mb-2">Resumen de Seguridad</h4>
              <ul className="space-y-2 text-sm text-blue-800">
                <li className="flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  <span>Esta acción ha sido registrada permanentemente en el sistema de auditoría.</span>
                </li>
                <li className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <span>La fecha y hora han sido registradas con precisión de segundos.</span>
                </li>
                {log.ipAddress && (
                  <li className="flex items-center gap-2">
                    <Globe className="h-4 w-4" />
                    <span>La dirección IP del administrador ha sido registrada: {log.ipAddress}</span>
                  </li>
                )}
              </ul>
            </div>
          </div>
  
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={onClose}>
              Cerrar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }